import { auth } from '@/config/firebase'
import { API_CONFIG } from '@/config/api'
import { ApiError } from './ApiError'

export type UnauthorizedHandler = () => void | Promise<void>

/**
 * HTTP client with three responsibilities beyond raw fetch:
 *
 * 1. Attaches a Firebase Bearer token (or a dev mock token in DEV mode).
 * 2. Normalizes every non-2xx response and network failure into ApiError.
 * 3. Notifies a registered `UnauthorizedHandler` when the server returns
 *    401, so the app can sign out + redirect exactly once even if several
 *    in-flight requests hit the 401 in the same tick.
 *
 * The handler is wired by the auth bootstrap (see useStartupBootstrap,
 * later commit). ApiClient itself stays decoupled from routing.
 */
class ApiClient {
  private baseURL: string
  private onUnauthorized: UnauthorizedHandler | null = null
  /** De-dup flag for concurrent 401s. Cleared after `dedupeWindowMs`. */
  private handling401 = false
  /** Short window so a fresh auth session can still trigger the handler. */
  private static readonly dedupeWindowMs = 1000

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL
  }

  /**
   * Register the handler called once per 401 burst. Pass `null` to clear.
   * Resets the dedupe flag so the next 401 in a fresh auth session fires
   * the handler even if a stale one was firing when this was called.
   */
  setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
    this.onUnauthorized = handler
    this.handling401 = false
  }

  /** Fire the registered unauthorized handler at most once per burst. */
  private notifyUnauthorized(): void {
    if (this.handling401) return
    const handler = this.onUnauthorized
    if (!handler) return
    this.handling401 = true
    Promise.resolve()
      .then(() => handler())
      .catch((err) => {
        console.error('[ApiClient] unauthorized handler threw:', err)
      })
      .finally(() => {
        setTimeout(() => {
          this.handling401 = false
        }, ApiClient.dedupeWindowMs)
      })
  }

  private async getAuthToken(): Promise<string | null> {
    if (!auth?.currentUser) {
      // In development, if no real user is signed in (e.g. using mock auth store),
      // return a mock token to allow bypassing backend auth checks.
      if (import.meta.env.DEV) {
        console.warn('⚠️ using mock auth token for dev environment')
        return 'MOCK_DEV_TOKEN'
      }
      return null
    }

    try {
      return await auth.currentUser.getIdToken()
    } catch (error) {
      console.error('Failed to get auth token:', error)
      return null
    }
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const url = `${this.baseURL}${endpoint}`

    try {
      const response = await fetch(url, {
        ...options,
        headers
      })

      if (!response.ok) {
        throw await ApiError.fromResponse(response)
      }

      // 204 No Content (and other empty bodies) — return undefined cast as
      // T rather than the raw Response. Callers typed as Promise<{...}>
      // would otherwise get a Response object and silently violate the
      // declared shape.
      if (response.status === 204) {
        return undefined as T
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }

      // Non-JSON 2xx with a body — same reasoning. Don't hand back a
      // Response object pretending to be T.
      return undefined as T
    } catch (error) {
      const apiError =
        error instanceof ApiError ? error : ApiError.fromNetworkError(error, url)
      if (apiError.isUnauthorized) this.notifyUnauthorized()
      throw apiError
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    let queryString = ''
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, value)
      })
      queryString = `?${searchParams.toString()}`
    }

    return this.makeRequest<T>(endpoint + queryString, {
      method: 'GET'
    })
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'DELETE'
    })
  }

  /** Single-file upload under FormData key `file` (legacy shape). */
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>
  ): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    return this.sendMultipart<T>(`${this.baseURL}${endpoint}`, formData)
  }

  /**
   * POST a pre-built FormData. Callers construct their own FormData, which
   * lets them append arbitrary keys and multiple files under the same key
   * (`files` for the /documents endpoint). Auth, 401 notification, and error
   * wrapping behave identically to the JSON-body methods.
   */
  async postMultipart<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.sendMultipart<T>(`${this.baseURL}${endpoint}`, formData)
  }

  /** Shared multipart POST pipeline — token, error wrap, 401 dedupe. */
  private async sendMultipart<T>(url: string, formData: FormData): Promise<T> {
    const token = await this.getAuthToken()

    const headers: Record<string, string> = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      })

      if (!response.ok) {
        throw await ApiError.fromResponse(response)
      }

      return await response.json()
    } catch (error) {
      const apiError =
        error instanceof ApiError ? error : ApiError.fromNetworkError(error, url)
      if (apiError.isUnauthorized) this.notifyUnauthorized()
      throw apiError
    }
  }
}

export const apiClient = new ApiClient()
